<template>
  <div class="shipIssues">
    <div class="shipIssues__bar">
      <div class="shipIssues__filters">
        <button
            v-for="f in flagFilters"
            :key="f.key"
            type="button"
            class="shipIssues__filter"
            :class="{ 'shipIssues__filter--active': flagFilter === f.key }"
            :style="flagFilter === f.key && f.color ? { background: f.color, borderColor: f.color, color: '#fff' } : {}"
            @click="flagFilter = f.key"
        >
          {{ f.label }} <span class="shipIssues__count">{{ countFor(f.key) }}</span>
        </button>
      </div>
      <v-spacer />
      <v-switch v-model="showAll" dense hide-details inset label="Alle aktiven Sendungen" class="shipIssues__switch" />
      <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" placeholder="Bestellung, Kunde, Tracking" class="shipIssues__search" />
    </div>

    <v-data-table
        :headers="headers"
        :items="rows"
        :items-per-page="50"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        :loading="loading"
        sort-by="lastEventAt"
        :sort-desc="false"
        dense
        class="shipIssues__table"
    >
      <template v-slot:[`item.orderName`]="{ item }">
        <a class="shipIssues__order" :class="{ 'shipIssues__order--express': item.isExpress }" @click="openOrder(item.orderUid)">{{ item.orderName }}</a>
        <div class="shipIssues__muted">{{ item.customerName }} · {{ item.countryCode }}</div>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span class="shipIssues__status" :style="{ color: statusMeta(item.status).color }">
          <v-icon x-small :color="statusMeta(item.status).color">{{ statusMeta(item.status).icon }}</v-icon>
          {{ statusMeta(item.status).label }}
        </span>
        <div v-if="item.flag" class="shipIssues__flag" :style="{ background: flagMeta(item.flag).color }">{{ flagMeta(item.flag).label }}</div>
      </template>
      <template v-slot:[`item.lastEventAt`]="{ item }">
        <div>{{ item.lastEventAt ? ago(item.lastEventAt) : item.shippingDate ? "versandt " + ago(item.shippingDate) : "—" }}</div>
        <div class="shipIssues__muted shipIssues__event" :title="item.lastEventText || ''">{{ item.lastEventText || "" }}</div>
      </template>
      <template v-slot:[`item.carrier`]="{ item }">
        <div>{{ item.fulfiller }}</div>
        <a v-if="item.trackingUrl" :href="item.trackingUrl" target="_blank" class="shipIssues__muted">{{ item.carrier }} · {{ item.trackingCode }}</a>
        <div v-else class="shipIssues__muted">{{ item.carrier }} · {{ item.trackingCode }}</div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn x-small text @click="openOrder(item.orderUid)">Bestellung</v-btn>
        <v-btn v-if="item.trackingUrl" x-small text :href="item.trackingUrl" target="_blank">Tracking</v-btn>
      </template>
      <template v-slot:no-data>
        <div class="shipIssues__empty">{{ showAll ? "Keine aktiven Sendungen" : "Keine auffälligen Sendungen — alles läuft" }}</div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// Sendungen mit Handlungsbedarf (17TRACK-Index shipments/{trackingCode}, nur Server schreibt):
// keine Bewegung, liegt im Paketshop, Zustellung fehlgeschlagen, Problem, Rücksendung, kein Scan.
// „Alle aktiven" zeigt zusätzlich die unauffälligen unterwegs.
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../../../fb";
import { getDateFromNow, openOrderModal } from "@/utils";
import { SHIPMENT_FLAG, shipmentFlagMeta, shipmentStatusMeta } from "@/utils/shipments";

export default {
  name: "ShipmentIssues",
  data() {
    return {
      flagged: [],
      active: [],
      showAll: false,
      flagFilter: "all",
      search: "",
      loading: true,
      unsubFlagged: null,
      unsubActive: null,
      headers: [
        { text: "Bestellung", value: "orderName", width: 200 },
        { text: "Status", value: "status", width: 220 },
        { text: "Letztes Ereignis", value: "lastEventAt" },
        { text: "Fulfiller / Carrier", value: "carrier", width: 260 },
        { text: "", value: "actions", sortable: false, width: 170, align: "end" },
      ],
    };
  },
  computed: {
    flagFilters() {
      return [{ key: "all", label: "Alle auffälligen", color: null }, ...Object.entries(SHIPMENT_FLAG).map(([key, m]) => ({ key, label: m.label, color: m.color }))];
    },
    source() {
      return this.showAll ? this.active : this.flagged;
    },
    rows() {
      const q = (this.search || "").trim().toLowerCase();
      return this.source.filter((s) => {
        if (this.flagFilter !== "all" && s.flag !== this.flagFilter) return false;
        if (!q) return true;
        return [s.orderName, s.customerName, s.trackingCode, s.carrier, s.countryCode].some((v) => String(v || "").toLowerCase().includes(q));
      });
    },
  },
  watch: {
    showAll(v) {
      if (v && !this.unsubActive) this.listenActive();
    },
  },
  mounted() {
    this.unsubFlagged = onSnapshot(query(collection(db, "shipments"), where("flagged", "==", true), where("active", "==", true)), (snap) => {
      this.flagged = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.loading = false;
      this.$emit("count", this.flagged.length);
    }, (e) => { console.error("shipments flagged", e); this.loading = false; });
  },
  beforeDestroy() {
    if (this.unsubFlagged) this.unsubFlagged();
    if (this.unsubActive) this.unsubActive();
  },
  methods: {
    listenActive() {
      this.unsubActive = onSnapshot(query(collection(db, "shipments"), where("active", "==", true)), (snap) => {
        this.active = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, (e) => console.error("shipments active", e));
    },
    countFor(key) {
      return key === "all" ? this.source.filter((s) => s.flag).length : this.source.filter((s) => s.flag === key).length;
    },
    statusMeta: shipmentStatusMeta,
    flagMeta: shipmentFlagMeta,
    ago(v) {
      return getDateFromNow(typeof v === "number" ? new Date(v) : v);
    },
    openOrder(uid) {
      openOrderModal(uid);
    },
  },
};
</script>

<style lang="scss" scoped>
.shipIssues {
  padding: 12px 16px 24px;
  &__bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
  &__filters { display: flex; gap: 6px; flex-wrap: wrap; }
  &__filter {
    border: 1px solid rgba(0, 0, 0, 0.14); border-radius: 999px; padding: 4px 10px; font-size: 12px; background: #fff; cursor: pointer;
    &--active { background: #282828; border-color: #282828; color: #fff; }
  }
  &__count { opacity: 0.7; margin-left: 2px; }
  &__switch { margin: 0 8px; }
  &__search { max-width: 280px; }
  &__order { font-weight: 600; cursor: pointer; &--express { color: #d8493f; } }
  &__muted { font-size: 11px; color: rgba(0, 0, 0, 0.55); }
  &__event { max-width: 420px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__status { font-weight: 600; font-size: 12px; display: inline-flex; align-items: center; gap: 4px; }
  &__flag { display: inline-block; margin-top: 3px; color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 0.02em; border-radius: 4px; padding: 1px 6px; text-transform: uppercase; }
  &__empty { padding: 24px; color: rgba(0, 0, 0, 0.5); }
}
</style>
